import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "./useAuth";
import { formatDistanceToNow } from "date-fns";

export interface UserSubscription {
  examSlug: string;
  examName: string;
  examIcon?: string;
  examColor?: string;
  subscriptionType: string | null;
  activatedAt: string;
  expiresAt: string | null;
  trialExpiresAt: string | null;
  trialStartedAt: string | null;
  hasStripeCustomer: boolean;
  autoRenew: boolean;
  isInTrial: boolean;
  isExpired: boolean;
  daysRemaining: number | null;
  status: 'trial' | 'active' | 'expired';
}

export const useAllUserSubscriptions = () => {
  const { user } = useAuth();

  const sb = supabase as any;

  const { data: subscriptions, isLoading, error } = useQuery({
    queryKey: ["all-user-subscriptions", user?.id],
    enabled: !!user?.id,
    queryFn: async () => {
      const { data: accessData, error: accessError } = await sb
        .from("user_exam_access_safe")
        .select(`
          exam_slug,
          subscription_type,
          activated_at,
          expires_at,
          trial_expires_at,
          trial_started_at,
          auto_renew,
          has_stripe_customer
        `)
        .eq("user_id", user!.id)
        .order("activated_at", { ascending: false });

      if (accessError) throw accessError;
      if (!accessData || accessData.length === 0) return [];

      // Fetch exam labels for all subscriptions
      const examSlugs = accessData.map((item: any) => item.exam_slug);
      const { data: labelsData, error: labelsError } = await supabase
        .from("exam_labels")
        .select("slug, name, icon, color")
        .in("slug", examSlugs);

      if (labelsError) throw labelsError;

      // Map labels for quick lookup
      const labelsMap = new Map(
        labelsData?.map(label => [label.slug, label]) || []
      );

      // Process and enrich subscription data
      const now = new Date();
      
      return accessData.map((access: any): UserSubscription => {
        const label = labelsMap.get(access.exam_slug);
        const trialExpiry = access.trial_expires_at ? new Date(access.trial_expires_at) : null;
        const paidExpiry = access.expires_at ? new Date(access.expires_at) : null;

        const isInTrial = !!(trialExpiry && trialExpiry > now);
        const hasPaidAccess = !!(paidExpiry && paidExpiry > now);
        const isExpired = !isInTrial && !hasPaidAccess;

        let daysRemaining: number | null = null;
        let status: 'trial' | 'active' | 'expired' = 'expired';

        if (isInTrial && trialExpiry) {
          daysRemaining = Math.ceil((trialExpiry.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
          status = 'trial';
        } else if (hasPaidAccess && paidExpiry) {
          daysRemaining = Math.ceil((paidExpiry.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
          status = 'active';
        }

        return {
          examSlug: access.exam_slug,
          examName: label?.name || access.exam_slug,
          examIcon: label?.icon,
          examColor: label?.color,
          subscriptionType: access.subscription_type,
          activatedAt: access.activated_at,
          expiresAt: access.expires_at,
          trialExpiresAt: access.trial_expires_at,
          trialStartedAt: access.trial_started_at,
          hasStripeCustomer: access.has_stripe_customer ?? false,
          autoRenew: access.auto_renew,
          isInTrial,
          isExpired,
          daysRemaining,
          status,
        };
      });
    },
  });

  return {
    subscriptions: subscriptions || [],
    loading: isLoading,
    error,
  };
};
